import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchWork, moveWork, type SeoWorkType } from './api';
import { WorkModal } from './WorkModal';
import { WorkTree } from './WorkTree';

/** `null` — closed; `parentId` set — create child (0 = section); otherwise edit the selected work. */
type ModalState = { mode: 'create'; parentId: number } | { mode: 'edit' } | null;

export const SeoWorksPage = () => {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  const [dragging, setDragging] = useState<SeoWorkType | null>(null);
  const [moveError, setMoveError] = useState('');

  const current = useQuery({
    queryKey: ['seo-work', 'view', selectedId],
    queryFn: () => fetchWork(selectedId as number),
    enabled: selectedId !== null,
  });
  const work = selectedId === null ? undefined : current.data;

  const move = useMutation({
    mutationFn: ({ id, parentId }: { id: number; parentId: number }) => moveWork(id, parentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-work'] }),
    onError: (error) => setMoveError(error.message),
  });

  const onDropOn = (source: SeoWorkType, target: SeoWorkType | null) => {
    const question = target
      ? `Вы пытаетесь изменить вложенность. Сделать "${source.name}" ребенком для "${target.name}".`
      : `Вы пытаетесь изменить вложенность. Сделать "${source.name}" корневым элементом.`;
    setDragging(null);
    if (!window.confirm(question)) return;
    setMoveError('');
    move.mutate({ id: source.id, parentId: target?.id ?? 0 });
  };

  return (
    <>
      <section className="content-header">
        <h1>Услуги</h1>
        <div className="row">
          <div className="col-sm-12">
            <button
              type="button"
              className="btn btn-info pull-right"
              onClick={() => setModal({ mode: 'create', parentId: 0 })}
            >
              Создать
            </button>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="row">
          <div className="col-md-4">
            <div className="box">
              <div className="box-body">
                <WorkTree
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  dragging={dragging}
                  onDragChange={setDragging}
                  onDropOn={onDropOn}
                />
              </div>
            </div>
          </div>
          <div className="col-md-8">
            <div className="box">
              <div className="box-header with-border">
                <h3 className="box-title">{work?.name || '-'}</h3>
              </div>
              <div className="box-body">
                <div>
                  <b>Id:</b> {work?.id || '-'}
                </div>
                <div>
                  <b>Parent id:</b> {work?.parent_id}
                </div>
                <div>
                  <b>Slug:</b> {work?.slug || '-'}
                </div>
                {work && (
                  <div style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn btn-info"
                      onClick={() => setModal({ mode: 'create', parentId: work.id })}
                    >
                      Create Child
                    </button>{' '}
                    <button type="button" className="btn btn-success" onClick={() => setModal({ mode: 'edit' })}>
                      Edit
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {moveError && <div className="alert alert-danger">{moveError}</div>}
      </section>
      {modal?.mode === 'create' && (
        <WorkModal parentId={modal.parentId} onClose={() => setModal(null)} />
      )}
      {modal?.mode === 'edit' && work && <WorkModal work={work} onClose={() => setModal(null)} />}
    </>
  );
};
